import {get} from "lodash";
import {IEvent, IEventWithPrevNext, IFantasyTile} from "modules/actions";
import {EventStatus, FantasyTileStatus, FantasyTileType} from "modules/enums";
import {getEvents, getViewedEvent} from "modules/selectors/events";
import {IStore} from "modules/types/store";
import {createSelector} from "reselect";

const getState = ({fantasyTiles}: IStore) => fantasyTiles;
export const getFullTeam = createSelector(getState, (state) => state.team);

export const getStatusFilter = createSelector(getState, (state) => state.statusFilter);
export const getFantasyTiles = createSelector(getState, (state) => state.tiles);
export const getLandingSettings = createSelector(getState, (state) => state.settings);

export const getTileById = createSelector(
	getFantasyTiles,
	(tiles) => (tileId: number | undefined) => tiles.find((tile) => tile.id === tileId)
);

export const getFantasySwings = createSelector(getFantasyTiles, (tiles) =>
	tiles.filter((tile) => tile.type === FantasyTileType.SWING)
);

export const getFantasyMajors = createSelector(getFantasyTiles, (tiles) =>
	tiles.filter((tile) => tile.type === FantasyTileType.MAJOR)
);

export const getFilteredFantasySwings = createSelector(
	getFantasySwings,
	getStatusFilter,
	(tiles, statusFilter) => {
		if (statusFilter.includes("all")) {
			return tiles;
		}

		return tiles.filter((tile) => statusFilter.includes(tile.status));
	}
);

export const getFilteredFantasyMajors = createSelector(
	getFantasyMajors,
	getStatusFilter,
	(tiles, statusFilter) => {
		if (statusFilter.includes("all")) {
			return tiles;
		}

		return tiles.filter((tile) => statusFilter.includes(tile.status));
	}
);

export const getFullFantasyEvent = createSelector(getFantasyTiles, (tiles) =>
	tiles.find((tile) => tile.type === FantasyTileType.FULL_FANTASY)
);

export const getIsFilterOpen = createSelector(getState, (state) => state.isFiltersOpen);

export const getIsParticipatedInTile = createSelector(
	getFullTeam,
	(team) => (tileId: number | undefined) => {
		if (!team || !tileId) {
			return false;
		}

		return team.tiles.some((tile) => tile.id === tileId);
	}
);

export const getIsTileScored = createSelector(
	getFantasyTiles,
	getFullTeam,
	(tiles, team) => (tileId: number | undefined) => {
		const tile = tiles.find((item) => item.id === tileId);

		const events = tile?.events;

		if (!events?.length || !team) {
			return false;
		}

		const eventIds = events.flatMap((event) => event.id);

		return Object.keys(team.scoreflow).some((eventId) => eventIds.includes(Number(eventId)));
	}
);

export const getParticipatedTiles = createSelector(getFantasyTiles, getFullTeam, (tiles, team) => {
	if (!tiles || !team || !team?.tiles) {
		return [];
	}

	return tiles.filter((tile) => team.tiles.some((teamTile) => tile.id === teamTile.id));
});

export const getEventPoints = createSelector(
	getFullTeam,
	(team) => (eventId: number) => get(team, `scoreflow.${eventId}`)
);

const findEventById = (events: IEvent[], eventId: number) => {
	return events.find((event) => event.id === eventId);
};

// Events are list with all non canceled events
export const getPrevEventAccordingTile = createSelector(
	getEvents,
	(events) => (event: IEventWithPrevNext | null, tile: IFantasyTile | undefined) => {
		if (!event) {
			return undefined;
		}

		if (!tile) {
			return event.prevEvent?.id;
		}

		// Filter to only non canceled events
		const filteredEvents = tile.events.filter((eventId) => findEventById(events, eventId.id));

		const index = filteredEvents.findIndex((tileEvent) => tileEvent.id === event.id);
		if (index === -1) {
			return undefined;
		}

		return filteredEvents[index - 1]?.id;
	}
);
export const getNextEventAccordingTile = createSelector(
	getEvents,
	(events) => (event: IEventWithPrevNext | null, tile: IFantasyTile | undefined) => {
		if (!event) {
			return undefined;
		}

		if (!tile) {
			return event.nextEvent?.id;
		}

		// Filter to only non canceled events
		const filteredEvents = tile.events.filter((eventId) => findEventById(events, eventId.id));

		const index = filteredEvents.findIndex((tileEvent) => tileEvent.id === event.id);
		if (index === -1) {
			return undefined;
		}

		return filteredEvents[index + 1]?.id;
	}
);

export const getSelectedTile = createSelector(getState, (state) => {
	return state.tiles.find((tile) => tile.id === state.selectedTile);
});

export const getSelectedTileEvents = createSelector(
	getSelectedTile,
	getEvents,
	(selectedTile, events) => {
		if (!selectedTile) {
			return [];
		}

		const eventsIds = selectedTile.events.flatMap((event) => event.id);
		return events.filter((event) => eventsIds.includes(event.id));
	}
);

export const getLastEventOfTile = createSelector(getSelectedTileEvents, (events) => {
	const complete = events.findLast((event) => event.status === EventStatus.Complete);
	const live = events.find((event) => event.status === EventStatus.Active);
	const schedule = events.find((event) => event.status === EventStatus.Scheduled);

	return live || schedule || complete;
});

// export const getViewedEventAccordingTile = createSelector(getViewedEvent, (tile, actualEvent))

export const getBannerTile = createSelector(
	getFantasyTiles,
	getSelectedTile,
	getFullTeam,
	(tiles, tile, team) => {
		const currentType = tile?.type;

		if (!currentType) return null;

		const nextTile = tiles.find((tile) => {
			if (currentType === FantasyTileType.FULL_FANTASY) {
				return (
					[FantasyTileType.MAJOR, FantasyTileType.SWING].includes(tile.type) &&
					[FantasyTileStatus.ACTIVE].includes(tile.status)
				);
			}

			return [FantasyTileType.FULL_FANTASY].includes(tile.type);
		});

		console.log(nextTile);

		const isPlayed = team?.tiles?.some((tile) => tile.id === nextTile?.id);

		if (isPlayed) return null;

		return nextTile;
	}
);

export const getViewedSwingId = createSelector(getViewedEvent, getFantasyTiles, (event, tiles) => {
	if (!event) return null;

	return tiles.find((tile) => {
		if (tile.type === FantasyTileType.FULL_FANTASY) return false;

		return tile.events.some((tileEvent) => tileEvent.id === event.id);
	})?.swingId;
});
